/**
 * DICHIARAZIONE DEI DIRITTI SULL'IMMAGINE (Týpos, 14 ott 2026). Davide: «aggiungi una casella prima del carrello».
 *
 * Solo nei modelli con il parametro "Foto" (linea Týpos): subito SOPRA il pulsante "Aggiungi al carrello" compare una
 * casella obbligatoria. Finche' non e' spuntata il carrello non parte (il clic viene fermato e la casella si evidenzia);
 * se il cliente cambia foto la casella si toglie e va rispuntata. Il pulsante e il riquadro prezzo li disegna l'App
 * Builder (e li ridisegna a ogni calcolo): la casella si rimette al suo posto da sola (osservatore della pagina).
 *
 * (14 ott 2026, Davide: «aggiungi la dichiarazione anche nella descrizione dell'ordine») al momento dell'invio al carrello
 * del negozio la descrizione che arriva dal modello riceve in fondo la dichiarazione con data e ora: si avvolge
 * addItemToCart dell'API e-commerce (stesso oggetto usato dal pulsante dell'App Builder). Solo Týpos; il clic arriva li'
 * solo con la casella spuntata.
 */
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {ECommerceApiSingleton} from "@AppBuilderLib/features/ecommerce/api/singleton";
import {IShapeDiverParameter} from "@AppBuilderLib/entities/parameter/config/parameter";
import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";

const NOME_FOTO = "Foto";
const OTTANIO = "#134D5D";
const TESTO = "Dichiaro di avere i diritti sull'immagine caricata e di poterla far incidere.";

/** la descrizione dell'ordine con la dichiarazione in fondo (data e ora del browser del cliente) */
export function descrizioneConDichiarazione(descrizione: string | undefined): string {
	const quando = new Date().toLocaleString("it-IT", {day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"});
	const dich = `Il cliente ha dichiarato di avere i diritti sull'immagine caricata e di poterla far incidere (${quando}).`;
	return descrizione && descrizione.trim() ? `${descrizione.trim()} · ${dich}` : dich;
}

/** avvolge una volta sola addItemToCart dell'API e-commerce: la descrizione riceve la dichiarazione */
let avvolto = false;
function avvolgiCarrello() {
	if (avvolto) return;
	avvolto = true;
	void ECommerceApiSingleton.then((api) => {
		const a = api as unknown as {addItemToCart: (d: {description?: string}) => Promise<unknown>};
		const originale = a.addItemToCart.bind(api);
		a.addItemToCart = (d) => originale({...d, description: descrizioneConDichiarazione(d.description)});
		(window as unknown as {__eidosCarrelloAvvolto: boolean}).__eidosCarrelloAvvolto = true;
	}).catch(() => undefined);
}

/** il pulsante "Aggiungi al carrello" disegnato dall'App Builder (il testo puo' essere in maiuscolo solo per lo stile) */
function pulsanteCarrello(): HTMLButtonElement | null {
	for (const b of document.querySelectorAll<HTMLButtonElement>("button")) {
		if (/aggiungi al carrello/i.test(b.textContent ?? "") && !b.closest(".eidos-diritti")) return b;
	}
	return null;
}

export default function EidosDirittiFoto() {
	const parameterStores = useShapeDiverStoreParameters((s) => s.parameterStores);
	const namespaces = Object.keys(parameterStores);
	const [conFoto, setConFoto] = useState(false);
	const [accettato, setAccettato] = useState(false);
	const accettatoRef = useRef(false);
	accettatoRef.current = accettato;
	const [evidenzia, setEvidenzia] = useState(false);
	const [box, setBox] = useState<HTMLElement | null>(null);

	// c'e' il parametro Foto? (Týpos) e la casella si toglie quando cambia la foto
	useEffect(() => {
		const {getParameter} = useShapeDiverStoreParameters.getState();
		const foto = namespaces.map((ns) => getParameter(ns, NOME_FOTO)).find(Boolean);
		setConFoto(!!foto);
		if (!foto) return;
		avvolgiCarrello();
		(window as unknown as {__eidosDiritti: unknown}).__eidosDiritti = {descrizione: descrizioneConDichiarazione};
		const chiave = () => {
			const v = (foto.getState() as IShapeDiverParameter<unknown>).state.uiValue;
			return v instanceof File ? `${v.name}|${v.size}|${v.lastModified}` : String(v ?? "");
		};
		let prima = chiave();
		return foto.subscribe(() => {
			const ora = chiave();
			if (ora === prima) return;
			prima = ora;
			setAccettato(false);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [namespaces.join("|")]);

	// contenitore della casella subito prima del pulsante del carrello (rimesso a posto a ogni ridisegno)
	useEffect(() => {
		if (!conFoto) return;
		let vivo = true;
		let el: HTMLElement | null = null;
		const sistema = () => {
			const b = pulsanteCarrello();
			if (!b) return;
			// subito prima del pulsante, nella stessa colonna (sotto nome e prezzo)
			const colonna = b.parentElement;
			if (!colonna) return;
			if (!el) {
				el = document.createElement("div");
				el.className = "eidos-diritti";
			}
			if (b.previousElementSibling !== el) colonna.insertBefore(el, b);
			b.dataset.eidosBloccato = accettatoRef.current ? "0" : "1";
			if (vivo) setBox(el);
		};
		sistema();
		let attesa: number | undefined;
		const oss = new MutationObserver(() => {
			if (attesa !== undefined) return;
			attesa = window.setTimeout(() => {
				attesa = undefined;
				if (vivo) sistema();
			}, 150);
		});
		oss.observe(document.body, {childList: true, subtree: true});
		// clic sul carrello senza la casella spuntata: fermato
		const ferma = (ev: MouseEvent) => {
			if (accettatoRef.current) return;
			const t = ev.target as HTMLElement | null;
			const b = t?.closest("button");
			if (!b || !/aggiungi al carrello/i.test(b.textContent ?? "")) return;
			ev.preventDefault();
			ev.stopPropagation();
			setEvidenzia(true);
			window.setTimeout(() => setEvidenzia(false), 2500);
		};
		document.addEventListener("click", ferma, true);
		return () => {
			vivo = false;
			oss.disconnect();
			if (attesa !== undefined) window.clearTimeout(attesa);
			document.removeEventListener("click", ferma, true);
			el?.remove();
		};
	}, [conFoto]);

	// aspetto del pulsante (sbiadito finche' la casella non e' spuntata)
	useEffect(() => {
		const b = pulsanteCarrello();
		if (b) b.dataset.eidosBloccato = accettato ? "0" : "1";
	}, [accettato, box]);

	if (!conFoto || !box) return null;
	return createPortal(
		<div className={"eidos-diritti-blocco" + (evidenzia ? " evidenzia" : "")}>
			{/* casella HTML semplice: questo componente sta FUORI dal MantineProvider (un componente Mantine qui fa cadere
			    tutta la pagina, 14 ott 2026) */}
			<label className="eidos-diritti-etichetta">
				<input type="checkbox" checked={accettato} onChange={(e) => setAccettato(e.currentTarget.checked)} style={{accentColor: OTTANIO}} />
				<span>{TESTO}</span>
			</label>
			{evidenzia && <div className="eidos-diritti-avviso">Spunta la casella per aggiungere al carrello.</div>}
		</div>,
		box,
	);
}
